import classNames from 'classnames'
import AgerasNavbar from 'client/components/AgerasAccounting/AgerasNavbar/AgerasNavbar'
import { getBetaAccessGroup } from 'client/components/AgerasAccounting/helpers'
import { PremiumModalOrigin } from 'client/Global/AnalyticsEvents'
import { type CompanyInterface, UiSetup } from 'client/Interfaces/CompanyInterface'
import { type TokenInterface } from 'client/Interfaces/TokenInterface'
import { shouldSeeSuperNavigation } from 'client/Other/functions/superNavigationFunctions'
import { l } from 'client/Other/Translation'
import * as React from 'react'
import { connect } from 'react-redux'
import BurgerMenuIcon from 'static/images/nav-burger-white.svg'

import { navigateAddModal, navigateRemoveModal, setNavigation } from '../actions/navigation'
import { Config } from '../config'
import { Container } from '../Global/Container'
import { type NavigationInterface } from '../Interfaces/NavigationInterface'
import { type SettingsInterface } from '../Interfaces/Settings/SettingsInterface'
import { type StateInterface } from '../Interfaces/StateInterface'
import { hasModuleAvailable, moduleNames } from '../reducers/settings'
import './Menu.scss'
import { Notifications } from './notifications/Notifications'

interface MenuItem {
  name: string
  label: string
  target?: string
  className?: string
  action?: () => void
}

interface StateProps {
  navigation: any
  brand: 'zervant' | 'ing'
  showNotificationsIcon: boolean
  token: TokenInterface
  settings: SettingsInterface
  company: CompanyInterface
  userCreationDate: number
  userEmail: string
  uiSetup: UiSetup
}

interface ActionProps {
  actions: {
    setNavigation: (navigation: NavigationInterface, uiSetup?: UiSetup) => void
    openSubscriptionModal: () => void
    openSupportModal: () => void
  }
}

export type MenuInterface = StateProps & ActionProps

class MenuClass extends Container<MenuInterface> {
  constructor(props) {
    super(props)
    this.state = {
      items: [] as MenuItem[],
      showWarning: false,
      navigationItem: null,
      burgerOpen: false,
    }
  }

  public componentDidMount() {
    const menuItems = this.getMainMenuItems()
    this.setState({ items: menuItems })
    document.body.addEventListener('click', this.closeBurger.bind(this))
  }

  public componentWillUnmount() {
    document.body.removeEventListener('click', this.closeBurger.bind(this), false)
  }

  public navigate(item) {
    if (item.action) {
      return item.action()
    }
    const navigation = {
      support: () => {
        this.props.actions.openSupportModal()
      },
      logout: () => {
        this.props.actions.setNavigation({ ...this.props.navigation, path: '/logout' }, this.props.uiSetup)
      },
      upgrade: () => {
        this.props.actions.openSubscriptionModal()
      },
      settings: () => {
        this.props.actions.setNavigation({
          ...this.props.navigation,
          path: '/myAccount',
        })
      },
    }
    if (navigation[item.name]) {
      return navigation[item.name]()
    }
    this.props.actions.setNavigation({ ...this.props.navigation, path: '/' + item.name })
  }

  private closeBurger() {
    this.setState({ burgerOpen: false })
  }

  private readonly getMainMenuItems = () => {
    let items: MenuItem[] = []

    const isTouchEnabled = () => {
      const userAgent = window.navigator.userAgent.toLowerCase()
      const isTouchEnabled = 'ontouchstart' in window || navigator.maxTouchPoints > 0
      return isTouchEnabled && (!!userAgent.match(/(iphone|ipod|ipad)/) || !!userAgent.match(/(android)/))
    }

    items.push({
      name: 'logout',
      label: 'Logout',
      className: isTouchEnabled() ? '' : 'right',
    })

    this.props.settings.modules?.forEach((module) => {
      if (module.menu && !module.hidden) {
        const item: MenuItem = {
          name: module.name,
          label: module.label,
        }

        switch (module.name) {
          case 'invoices':
            item.className = 'main-menu-invoices'
            break
          case 'settings':
            item.className = isTouchEnabled() ? '' : 'right'
            break
          case 'support':
            item.target = '_blank'
            item.className = isTouchEnabled() ? '' : 'right'
            break
          case 'upgrade':
            item.className = 'right'
            break
        }
        items.push(item)
      }
    })

    if (
      this.props.company?.agerasNextMigration &&
      this.props.company.country === 'FR' &&
      this.props.company.uiSetup !== UiSetup.Invoicing
    ) {
      items = items.filter((s) => s.name !== 'banking')
    }

    const isAccountingModulesActive =
      hasModuleAvailable(this.props.settings, moduleNames.agerasAccounting) ||
      hasModuleAvailable(this.props.settings, moduleNames.agerasDirectAccounting)

    if (isAccountingModulesActive) {
      items.push(
        items.splice(
          items.findIndex((item) => ['agerasAccounting', 'agerasDirectAccounting'].includes(item.name)),
          1,
        )[0],
      )
    }

    if (
      (getBetaAccessGroup(this.props.company) === 'Group 1' || this.props.company?.agerasNextMigration) &&
      !isAccountingModulesActive &&
      shouldSeeSuperNavigation(this.props.company)
    ) {
      items.push({
        name: 'agerasAccounting',
        label: 'ageras.accounting',
        className: 'left',
      })
    }

    const workspace = Config.getVarsFromWebpackDefinePlugin().workspace
    if (workspace === 'local' || workspace === 'test') {
      items.push({
        name: 'gallery',
        label: 'Gallery',
        className: 'right',
      })
    }

    return items
  }

  private addMenuItems(items, current, menuName) {
    const invoiceSubTabs = ['reactInvoiceList', 'reactRecurringProfiles', 'receipts', 'deliverynotes']
    return items.map((item) => {
      const { className, name, target, href, label } = item
      const classes = ['link']

      const id = `${name}-tab`
      if (className) {
        classes.push(className)
      }
      if (name === current || (invoiceSubTabs.includes(current) && invoiceSubTabs.includes(name))) {
        classes.push('active')
      }

      return (
        <a
          title={l(`mainmenu.${label}`)}
          data-automation={`${menuName}-${item.name}`}
          href={href || ''}
          target={target || ''}
          key={name}
          className={classes.join(' ')}
          id={id}
          onClick={(event) => {
            this.setState({ burgerOpen: false })
            if (!href) {
              this.navigate(item)
              event.preventDefault()
            }
          }}
        >
          {l(`mainmenu.${label}`)}
        </a>
      )
    })
  }

  public render() {
    const current = this.props.navigation.path.split('/')[1]
    const leftMenu = this.state.items.filter((item) => !item.className || item.className.indexOf('right') < 0)
    const rightMenu = this.state.items.filter((item) => item.className && item.className.indexOf('right') >= 0)
    const agerasNextMigration = this.props.company?.agerasNextMigration ?? false

    const canSeeSidebar = shouldSeeSuperNavigation(this.props.company)

    return (
      <>
        {canSeeSidebar && (
          <div className="header-sidebar">
            <AgerasNavbar agerasNextMigration={agerasNextMigration} />
          </div>
        )}
        <div
          className={classNames('header-toolbar', {
            agerasNextMigration: canSeeSidebar && 'agerasNextMigration',
          })}
        >
          <div id="logo" />
          <div className="Menu horizontal" data-automation="menu-mainmenu">
            <div className="burger">
              <a
                className="link"
                onClick={() => {
                  this.setState({ burgerOpen: !this.state.burgerOpen })
                }}
              >
                <img src={BurgerMenuIcon} />
              </a>
              <div className={'burgerMenu' + (this.state.burgerOpen ? '' : ' hidden')}>
                {this.addMenuItems(leftMenu, current, 'burgermenu')}
              </div>
            </div>
            <div className="left">{this.addMenuItems(leftMenu, current, 'mainmenu')}</div>
            <div className="right">
              {this.addMenuItems(rightMenu, current, 'mainmenu')}
              {this.props.showNotificationsIcon && <Notifications />}
            </div>
          </div>
        </div>
      </>
    )
  }
}

const mapStateToProps = (state: StateInterface): StateProps => ({
  navigation: state.navigation,
  brand: state.token.prn.provider,
  showNotificationsIcon: state.settings && hasModuleAvailable(state.settings, moduleNames.reactDashboard),
  token: state.token,
  settings: state.settings,
  company: state.company,
  userCreationDate: state.token.creationDate,
  userEmail: state.user.email,
  uiSetup: state.company.uiSetup,
})

const mapDispatchToProps = (dispatch): ActionProps => ({
  actions: {
    setNavigation: (navigation, uiSetup) => dispatch(setNavigation(navigation, uiSetup)),
    openSubscriptionModal: () => {
      dispatch(
        navigateAddModal({
          component: 'PremiumPlanModal',
          props: {
            origin: PremiumModalOrigin.MainMenu,
            isOpen: true,
          },
        }),
      )
    },
    openSupportModal: () => {
      dispatch(
        navigateAddModal({
          component: 'SupportModal',
          props: {
            isOpen: true,
            close: () => dispatch(navigateRemoveModal('SupportModal')),
          },
        }),
      )
    },
  },
})

export const Menu = connect<StateProps, ActionProps>(mapStateToProps, mapDispatchToProps)(MenuClass)
